import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { BackstageState } from "../../redux/store";
import * as EventActions from "../../redux/actions/event.actions";
import Input from "@sellout/ui/build/components/Input";
import { Colors, Icon, Icons } from "@sellout/ui";
import useSeason from "../../hooks/useSeason.hook";
import { VariantEnum } from "../../../src/models/enums/VariantEnum";
import * as SeasonActions from "../../redux/actions/season.actions";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import ReactTooltip from "react-tooltip";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FieldRow = styled.div`
  position: relative;
  display: flex;
  input {
    width: 400px;
    ${media.mobile`
      width:100%;
    `};
  }
`;

enum YouTubeHoverText {
  Text = " Enter the alpha-numeric code from the end of the YouTube url: https://www.youtube.com/watch?v=123XYZ",
}

enum VimeoHoverText {
  Text = " Enter the numeric video ID from the end of the Vimeo url: https://vimeo.com/123456789",
}

const YOUTUBE_EMBED_BASE = `https://www.youtube.com/embed/`;
const VIMEO_EMBED_BASE = `https://player.vimeo.com/video/`;

type CreateEventVideoLinkProps = { type: string };

const CreateEventVideoLink: React.FC<CreateEventVideoLinkProps> = ({
  type,
}) => {
  /* State */
  const eventState = useSelector((state: BackstageState) => state.event);
  const { eventId, eventsCache } = eventState;
  const { season, seasonId } = useSeason();
  const event = eventsCache[eventId];
  const eventPerformance = event?.performances?.[0];
  const seasonPerformance = season?.performances?.[0];

  const videoLink: string =
    (type === VariantEnum.Event
      ? eventPerformance?.videoLink
      : seasonPerformance?.videoLink) ?? "";

  const vimeoLink: string =
    (type === VariantEnum.Event
      ? eventPerformance?.vimeoLink
      : seasonPerformance?.vimeoLink) ?? "";

  const [copiedYoutube, setCopiedYoutube] = useState(false);
  const [copiedVimeo, setCopiedVimeo] = useState(false);

  /* Actions */
  const dispatch = useDispatch();

  const setEventPerformanceVideoLink = (link: string) =>
    dispatch(
      EventActions.setEventPerformanceVideoLink(
        eventId,
        eventPerformance?._id as string,
        link
      )
    );

  const setSeasonPerformanceVideoLink = (link: string) =>
    dispatch(
      SeasonActions.setSeasonPerformanceVideoLink(
        seasonId,
        seasonPerformance?._id as string,
        link
      )
    );

  const setEventPerformanceVimeoLink = (link: string) =>
    dispatch(
      EventActions.setEventPerformanceVimeoLink(
        eventId,
        eventPerformance?._id as string,
        link
      )
    );

  const setSeasonPerformanceVimeoLink = (link: string) =>
    dispatch(
      SeasonActions.setSeasonPerformanceVimeoLink(
        seasonId,
        seasonPerformance?._id as string,
        link
      )
    );

  const copyToClipBoard = (text: string, kind: "youtube" | "vimeo") => {
    try {
      navigator.clipboard.writeText(text);
      if (kind === "youtube") setCopiedYoutube(true);
      else setCopiedVimeo(true);
    } catch {
      if (kind === "youtube") setCopiedYoutube(false);
      else setCopiedVimeo(false);
    }
  };

  useEffect(() => {
    if (copiedYoutube) {
      setTimeout(() => {
        setCopiedYoutube(false);
        ReactTooltip.rebuild();
      }, 2000);
    }
  }, [copiedYoutube]);

  useEffect(() => {
    if (copiedVimeo) {
      setTimeout(() => {
        setCopiedVimeo(false);
        ReactTooltip.rebuild();
      }, 2000);
    }
  }, [copiedVimeo]);

  /** Render */
  return (
    <Container>
      {/* ── YouTube Video ── */}
      <FieldRow>
        <div style={{ position: "absolute", left: "138px" }}>
          {" "}
          <Icon
            icon={Icons.QuestionSquareSolid}
            color={Colors.Grey4}
            hoverColor={Colors.Grey1}
            size={17}
            tip={YouTubeHoverText.Text}
          />
        </div>
        <Input
          label="YouTube Video"
          type="url"
          subLabel="(optional)"
          icon={Icons.LinkRegular}
          placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
          value={`${YOUTUBE_EMBED_BASE}${videoLink}`}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            if (YOUTUBE_EMBED_BASE.match(e.currentTarget.value)) {
              type === VariantEnum.Event
                ? setEventPerformanceVideoLink("")
                : setSeasonPerformanceVideoLink("");
            } else {
              const val = e.currentTarget.value
                .replace(YOUTUBE_EMBED_BASE, "")
                .replace(/\s+/g, "");
              type === VariantEnum.Event
                ? setEventPerformanceVideoLink(val)
                : setSeasonPerformanceVideoLink(val);
            }
          }}
        />
        {copiedYoutube ? (
          <span style={{ display: "flex" }}>
            Copied!{" "}
            <Icon icon={Icons.CheckCircle} color={Colors.Green} size={15} />
          </span>
        ) : (
          <Icon
            icon={Icons.CopyRegular}
            color={Colors.Grey4}
            hoverColor={Colors.Grey1}
            size={17}
            tip={"Copy"}
            onClick={() =>
              copyToClipBoard(`${YOUTUBE_EMBED_BASE + videoLink}`, "youtube")
            }
          />
        )}
      </FieldRow>

      {/* ── Vimeo Video ── */}
      <FieldRow>
        <div style={{ position: "absolute", left: "125px" }}>
          {" "}
          <Icon
            icon={Icons.QuestionSquareSolid}
            color={Colors.Grey4}
            hoverColor={Colors.Grey1}
            size={17}
            tip={VimeoHoverText.Text}
          />
        </div>
        <Input
          label="Vimeo Video"
          type="url"
          subLabel="(optional)"
          icon={Icons.LinkRegular}
          placeholder="https://vimeo.com/123456789"
          value={`${VIMEO_EMBED_BASE}${vimeoLink}`}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            if (VIMEO_EMBED_BASE.match(e.currentTarget.value)) {
              type === VariantEnum.Event
                ? setEventPerformanceVimeoLink("")
                : setSeasonPerformanceVimeoLink("");
            } else {
              const val = e.currentTarget.value
                .replace(VIMEO_EMBED_BASE, "")
                .replace(/\s+/g, "");
              type === VariantEnum.Event
                ? setEventPerformanceVimeoLink(val)
                : setSeasonPerformanceVimeoLink(val);
            }
          }}
        />
        {copiedVimeo ? (
          <span style={{ display: "flex" }}>
            Copied!{" "}
            <Icon icon={Icons.CheckCircle} color={Colors.Green} size={15} />
          </span>
        ) : (
          <Icon
            icon={Icons.CopyRegular}
            color={Colors.Grey4}
            hoverColor={Colors.Grey1}
            size={17}
            tip={"Copy"}
            onClick={() =>
              copyToClipBoard(`${VIMEO_EMBED_BASE + vimeoLink}`, "vimeo")
            }
          />
        )}
      </FieldRow>
    </Container>
  );
};

export default CreateEventVideoLink;
